import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import styles from './AuthPages.module.css';
import { brandConfig } from '../../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { appPath, termsHref, isExternalTerms } from '../../utils/navigation';

export default function ResetPasswordPage() {
  // ライトテーマ (usodeki 等) では認証カードを明色化する
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light}` : ''}`;
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');

  useEffect(() => {
    // Supabase Auth はハッシュフラグメントでトークンを渡す
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);

    const token = params.get('access_token');
    const refresh = params.get('refresh_token');

    if (token) {
      setAccessToken(token);
      setRefreshToken(refresh || '');
    } else {
      setError('無効なリセットリンクです。もう一度パスワードリセットをリクエストしてください。');
    }
  }, []);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'パスワードは8文字以上必要です';
    if (!/[A-Z]/.test(pwd)) return '大文字を含めてください';
    if (!/[a-z]/.test(pwd)) return '小文字を含めてください';
    if (!/[0-9]/.test(pwd)) return '数字を含めてください';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    const pwdError = validatePassword(password);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/email/reset-password', {
        access_token: accessToken,
        refresh_token: refreshToken,
        new_password: password,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(errorMessage || 'パスワードのリセットに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>パスワードを変更しました</h2>
              <p>
                新しいパスワードでログインできます。
              </p>
              <button
                className={styles.backButton}
                onClick={() => navigate('/', { replace: true })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                ログインページへ
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>新しいパスワードを設定</h1>
          <p className={styles.subtitle}>新しいパスワードを入力してください</p>

          {error && <div className={styles.error}>{error}</div>}

          {accessToken && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="password">新しいパスワード</label>
                <input
                  type="password"
                  id="password"
                  className={styles.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  autoComplete="new-password"
                />
                <span className={styles.hint}>8文字以上、大文字・小文字・数字を含む</span>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="confirmPassword">パスワード（確認）</label>
                <input
                  type="password"
                  id="confirmPassword"
                  className={styles.input}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="********"
                  required
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className={styles.submitButton} disabled={loading}>
                {loading ? '変更中...' : 'パスワードを変更'}
              </button>
            </form>
          )}

          <div className={styles.links}>
            <a href={appPath('/')} className={styles.link}>ログインページへ戻る</a>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
            <span className={styles.footerDivider}>|</span>
            <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
